"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.CombobulationDimension = void 0;
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
class CombobulationDimension {
    constructor(name, opts = {}) {
        this.name = name;
        this.points = new Map();
        this.wireObjects = new Map();
        this.wireGrid = new Map();
        this.pointRoundingPrecision = opts.pointRoundingPrecision ?? 0.1;
        this.connectionTolerance = opts.connectionTolerance ?? this.pointRoundingPrecision;
        this.boundingMargin = Math.max(this.pointRoundingPrecision, this.connectionTolerance);
        this.explicitWireIntersections = opts.explicitWireIntersections ?? false;
        this.wireCellSize = this.boundingMargin * 40;
    }
    addPoint(position, obj) {
        position = [this.roundPoint(position[0]), this.roundPoint(position[1])];
        if (!this.points.has(position[0])) {
            this.points.set(position[0], new Map());
        }
        const xMap = this.points.get(position[0]);
        if (!xMap.has(position[1])) {
            xMap.set(position[1], [obj]);
        }
        else {
            xMap.get(position[1])?.push(obj);
        }
    }
    addWireObject(obj) {
        if (!obj.wire) {
            throw new Error('addWireObject called on a non-wire object');
        }
        const localEndpoints = obj.endpoints.filter((ep) => ep.dimension === this.name);
        const geometry = localEndpoints.length >= 2 ? this.getWirePositions(obj.wire) : null;
        this.wireObjects.set(obj, { geometry, intersecting: [] });
        if (geometry) {
            this.addWireToGrid(obj, geometry);
        }
        const intersecting = Array.from(this.computeWiresNetObjects([obj]));
        this.wireObjects.get(obj).intersecting = intersecting;
    }
    getObjectsAtPosition(pos) {
        const x = [];
        x.push(this.points.get(pos[0]));
        x.push(this.points.get(this.roundPoint(pos[0])));
        return Array.from(new Set([
            ...this.computeWiresNetObjects(this.getWiresNearPosition(pos), pos),
            ...x
                .flatMap((x) => [x?.get(pos[1]), x?.get(this.roundPoint(pos[1]))])
                .flatMap((nos) => nos?.flatMap((no) => [no, no.wire ? this.wireObjects.get(no)?.intersecting : undefined]) ?? [])
                .filter((no) => !!no)
                .flat(),
        ]));
    }
    *iteratePointGroups() {
        for (const [x, yMap] of this.points.entries()) {
            for (const [y, objects] of yMap.entries()) {
                yield { x, y, objects };
            }
        }
    }
    getObjectsConnectedToPosition(startPos) {
        const visited = new Set();
        const positionQueue = [startPos];
        const visitedPositions = new Set();
        while (positionQueue.length > 0) {
            const pos = positionQueue.shift();
            const rx = this.roundPoint(pos[0]);
            const ry = this.roundPoint(pos[1]);
            const posKey = `${rx},${ry}`;
            if (visitedPositions.has(posKey)) {
                continue;
            }
            visitedPositions.add(posKey);
            for (const obj of this.getObjectsAtPosition(pos)) {
                if (visited.has(obj)) {
                    continue;
                }
                visited.add(obj);
                if (obj.wire) {
                    const geometry = this.getEffectiveWireGeometry(obj);
                    if (geometry) {
                        positionQueue.push(geometry.from);
                        positionQueue.push(geometry.to);
                        for (const intersecting of this.computeWiresNetObjects([obj])) {
                            if (!visited.has(intersecting)) {
                                visited.add(intersecting);
                                if (intersecting.wire) {
                                    const intGeometry = this.getEffectiveWireGeometry(intersecting);
                                    if (intGeometry) {
                                        positionQueue.push(intGeometry.from);
                                        positionQueue.push(intGeometry.to);
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
        return visited;
    }
    removeObject(obj) {
        for (const [x, yMap] of this.points.entries()) {
            for (const [y, objects] of yMap.entries()) {
                const idx = objects.indexOf(obj);
                if (idx !== -1) {
                    objects.splice(idx, 1);
                    if (objects.length === 0) {
                        yMap.delete(y);
                    }
                }
            }
            if (yMap.size === 0) {
                this.points.delete(x);
            }
        }
        this.removeWireFromGrid(obj);
        this.wireObjects.delete(obj);
        for (const [, entry] of this.wireObjects.entries()) {
            const idx = entry.intersecting.indexOf(obj);
            if (idx !== -1) {
                entry.intersecting.splice(idx, 1);
            }
        }
    }
    getConnectedWireGroups() {
        const visited = new Set();
        const groups = new Set();
        for (const wireObj of this.wireObjects.keys()) {
            if (visited.has(wireObj)) {
                continue;
            }
            const localEndpoint = wireObj.endpoints.find((ep) => ep.dimension === this.name);
            const startPos = localEndpoint?.position ?? wireObj.wire.from;
            const allConnected = this.getObjectsConnectedToPosition(startPos);
            const wireGroup = new Set();
            for (const obj of allConnected) {
                if (obj.wire) {
                    wireGroup.add(obj);
                    visited.add(obj);
                }
            }
            if (wireGroup.size > 0) {
                groups.add(wireGroup);
            }
        }
        return groups;
    }
    getWireIntersecting(obj) {
        return this.wireObjects.get(obj)?.intersecting;
    }
    *iterateWireObjects() {
        yield* this.wireObjects.keys();
    }
    *iterateLabelObjects(labelType) {
        for (const obj of this.iterateAllObjects()) {
            if (obj.label && (!labelType || obj.label.labelType === labelType)) {
                yield obj;
            }
        }
    }
    *iterateConnectionObjects() {
        for (const obj of this.iterateAllObjects()) {
            if (obj.connection) {
                yield obj;
            }
        }
    }
    *iterateAllObjects() {
        const seen = new Set();
        for (const [, yMap] of this.points.entries()) {
            for (const [, objects] of yMap.entries()) {
                for (const obj of objects) {
                    if (!seen.has(obj)) {
                        seen.add(obj);
                        yield obj;
                    }
                }
            }
        }
    }
    get allPoints() {
        return this.points;
    }
    roundPoint(point) {
        return (0, Transform_1.ceilToMultiple)(parseFloat(parseFloat(point.toFixed(3)).toFixed(2)), this.pointRoundingPrecision);
    }
    getEffectiveWireGeometry(obj) {
        const entry = this.wireObjects.get(obj);
        if (entry) {
            return entry.geometry;
        }
        if (!obj.wire) {
            return null;
        }
        return this.getWirePositions(obj.wire);
    }
    computeWiresNetObjects(wires, onPos) {
        const wireNetObjs = new Set();
        for (const obj of wires) {
            if (!obj.wire) {
                continue;
            }
            const wirePositions = this.getEffectiveWireGeometry(obj);
            if (!wirePositions) {
                continue;
            }
            if (onPos && !this.doesWireIntersect(wirePositions, onPos)) {
                continue;
            }
            wireNetObjs.add(obj);
            for (const [x, ys] of this.points.entries()) {
                if (Math.min(wirePositions.from[0], wirePositions.to[0]) > x + this.boundingMargin ||
                    Math.max(wirePositions.from[0], wirePositions.to[0]) < x - this.boundingMargin) {
                    continue;
                }
                for (const point of this.detectWireIntersections(wirePositions, Array.from(ys.keys()).map((y) => [x, y]))) {
                    for (const obj of ys.get(point[1]) ?? []) {
                        wireNetObjs.add(obj);
                    }
                }
            }
        }
        return wireNetObjs;
    }
    getWirePositions(wire) {
        const from = wire.from;
        const to = wire.to;
        return { from, to };
    }
    doesWireIntersect(wire, position) {
        for (const _ of this.detectWireIntersections(wire, [position])) {
            return true;
        }
        return false;
    }
    *detectWireIntersections(wire, positions) {
        const minX = Math.min(wire.from[0], wire.to[0]);
        const minY = Math.min(wire.from[1], wire.to[1]);
        const maxX = Math.max(wire.from[0], wire.to[0]);
        const maxY = Math.max(wire.from[1], wire.to[1]);
        if (this.explicitWireIntersections) {
            for (const pos of positions) {
                const dxFrom = wire.from[0] - pos[0];
                const dyFrom = wire.from[1] - pos[1];
                if (Math.sqrt(dxFrom * dxFrom + dyFrom * dyFrom) <= this.connectionTolerance) {
                    yield pos;
                }
                const dxTo = wire.to[0] - pos[0];
                const dyTo = wire.to[1] - pos[1];
                if (Math.sqrt(dxTo * dxTo + dyTo * dyTo) <= this.connectionTolerance) {
                    yield pos;
                }
            }
        }
        else {
            for (const pos of positions) {
                if (!this.pointInRect(wire.from, wire.to, pos, minX, minY, maxX, maxY)) {
                    continue;
                }
                const dx = pos[0] - wire.from[0];
                const dy = pos[1] - wire.from[1];
                const dot = dx * (wire.to[0] - wire.from[0]) + dy * (wire.to[1] - wire.from[1]);
                const len_sq = (wire.to[0] - wire.from[0]) * (wire.to[0] - wire.from[0]) +
                    (wire.to[1] - wire.from[1]) * (wire.to[1] - wire.from[1]);
                const param = dot / len_sq;
                const xx = wire.from[0] + param * (wire.to[0] - wire.from[0]);
                const yy = wire.from[1] + param * (wire.to[1] - wire.from[1]);
                const dx2 = xx - pos[0];
                const dy2 = yy - pos[1];
                const dist = Math.sqrt(dx2 * dx2 + dy2 * dy2);
                if (dist <= this.connectionTolerance) {
                    yield pos;
                }
            }
        }
    }
    pointInRect(from, to, pos, minX = Math.min(from[0], to[0]), minY = Math.min(from[1], to[1]), maxX = Math.max(from[0], to[0]), maxY = Math.max(from[1], to[1])) {
        const delta = this.boundingMargin;
        return pos[0] >= minX - delta && pos[0] <= maxX + delta && pos[1] >= minY - delta && pos[1] <= maxY + delta;
    }
    gridKey(cx, cy) {
        return `${cx},${cy}`;
    }
    addWireToGrid(obj, geometry) {
        const delta = this.boundingMargin;
        const minCx = Math.floor((Math.min(geometry.from[0], geometry.to[0]) - delta) / this.wireCellSize);
        const maxCx = Math.floor((Math.max(geometry.from[0], geometry.to[0]) + delta) / this.wireCellSize);
        const minCy = Math.floor((Math.min(geometry.from[1], geometry.to[1]) - delta) / this.wireCellSize);
        const maxCy = Math.floor((Math.max(geometry.from[1], geometry.to[1]) + delta) / this.wireCellSize);
        for (let cx = minCx; cx <= maxCx; cx++) {
            for (let cy = minCy; cy <= maxCy; cy++) {
                const key = this.gridKey(cx, cy);
                const cell = this.wireGrid.get(key);
                if (!cell) {
                    this.wireGrid.set(key, obj);
                }
                else if (cell instanceof Set) {
                    cell.add(obj);
                }
                else if (cell !== obj) {
                    this.wireGrid.set(key, new Set([cell, obj]));
                }
            }
        }
    }
    removeWireFromGrid(obj) {
        for (const [key, cell] of this.wireGrid.entries()) {
            if (cell instanceof Set) {
                if (!cell.delete(obj)) {
                    continue;
                }
                if (cell.size === 1) {
                    const [remaining] = cell;
                    this.wireGrid.set(key, remaining);
                }
                else if (cell.size === 0) {
                    this.wireGrid.delete(key);
                }
            }
            else if (cell === obj) {
                this.wireGrid.delete(key);
            }
        }
    }
    getWiresNearPosition(pos) {
        const cx = Math.floor(pos[0] / this.wireCellSize);
        const cy = Math.floor(pos[1] / this.wireCellSize);
        const result = new Set();
        for (let dx = -1; dx <= 1; dx++) {
            for (let dy = -1; dy <= 1; dy++) {
                const cell = this.wireGrid.get(this.gridKey(cx + dx, cy + dy));
                if (cell instanceof Set) {
                    for (const obj of cell) {
                        result.add(obj);
                    }
                }
                else if (cell) {
                    result.add(cell);
                }
            }
        }
        return result;
    }
}
exports.CombobulationDimension = CombobulationDimension;
//# sourceMappingURL=CombobulationDimension.js.map